import {
  E_ODATA_FILTER_UNEXPECTED_CHARACTER,
  E_ODATA_FILTER_UNEXPECTED_TYPE,
  E_ODATA_FILTER_UNEXPECTED_END_OF_INPUT,
  E_ODATA_UNKNOWN_PARAMETER_ALIAS,
  E_ODATA_FILTER_UNEXPECTED_TOKEN,
  E_ODATA_FILTER_INAPPLICABLE_NODE_TYPE,
  E_ODATA_FILTER_UNKNOWN_LOGICAL_OPERATOR,
  E_ODATA_FILTER_UNKNOWN_COMPARISON_OPERATOR,
  E_ODATA_FILTER_INVALID_COMPARISON_SIDE,
  E_ODATA_FILTER_UNEVALUATABLE_NODE_TYPE,
  E_ODATA_FILTER_UNSUPPORTED_FUNCTION_COMPARISON,
  E_ODATA_FILTER_EXPECTED_COLUMN_REFERENCE,
} from '@nhtio/lucid-resourceful/errors'
import type { HttpContext } from '@adonisjs/core/http'
import type { ResourcefulQueryScopeCallback } from './mixin'
import type { ApplicationService } from '@adonisjs/core/types'
import type { DatabaseQueryBuilderContract } from '@adonisjs/lucid/types/querybuilder'

interface Token {
  type:
    | 'IDENTIFIER'
    | 'STRING'
    | 'NUMBER'
    | 'BOOLEAN'
    | 'NULL'
    | 'OPERATOR'
    | 'LPAREN'
    | 'RPAREN'
    | 'COMMA'
    | 'WHITESPACE'
    | 'PARAMETER'
    | 'DATE'
    | 'GUID'
  value: string
}

interface ASTNode {
  type: string
  [key: string]: any
}

export type Dialect = 'postgres' | 'mysql' | 'sqlite'

export class ODataToLucidTranslator {
  static readonly #LOGICAL_OPERATORS = ['and', 'or', 'not']
  static readonly #COMPARISON_OPERATORS = ['eq', 'ne', 'gt', 'ge', 'lt', 'le', 'in', 'has']
  static readonly #STRING_FUNCTIONS = [
    'contains',
    'endswith',
    'startswith',
    'length',
    'indexof',
    'substring',
    'tolower',
    'toupper',
    'trim',
    'concat',
  ]
  static readonly #DATE_FUNCTIONS = ['year', 'month', 'day', 'hour', 'minute', 'second']
  static readonly #MATH_FUNCTIONS = ['round', 'floor', 'ceiling']

  #tokens: Token[] = []
  #position: number = 0
  #filter: string
  #aliases: Map<string, string>
  #keyToColumn: Map<string, string>
  #dialectType: Dialect

  constructor(
    filter: string,
    aliases: Map<string, string>,
    keyToColumn: Map<string, string>,
    dialect: Dialect = 'postgres'
  ) {
    this.#filter = filter
    this.#aliases = aliases
    this.#keyToColumn = keyToColumn
    this.#dialectType = dialect
  }

  public static apply(
    $filter: string,
    aliases: Map<string, string> = new Map(),
    keyToColumn: Map<string, string> = new Map(),
    dialect: Dialect = 'postgres',
    dbQuery: DatabaseQueryBuilderContract
  ): DatabaseQueryBuilderContract {
    const translator = new ODataToLucidTranslator($filter, aliases, keyToColumn, dialect)
    return translator.#translate(dbQuery)
  }

  public static toHook(
    $filter: string,
    aliases: Map<string, string> = new Map(),
    keyToColumn: Map<string, string> = new Map(),
    dialect: Dialect = 'postgres'
  ): ResourcefulQueryScopeCallback {
    const bound = ODataToLucidTranslator.apply.bind(null, $filter, aliases, keyToColumn, dialect)
    return (_ctx: HttpContext, _app: ApplicationService, query: DatabaseQueryBuilderContract) => {
      bound(query)
    }
  }

  #getSqlFns(query?: DatabaseQueryBuilderContract) {
    // Prefer the actual DB client from the query when available; fall back to configured dialect
    let resolved: Dialect = this.#dialectType
    if (query) {
      const client = this.#dialect(query)
      if (client === 'pg') resolved = 'postgres'
      else if (client === 'mysql' || client === 'mysql2') resolved = 'mysql'
      else if (client === 'sqlite3') resolved = 'sqlite'
    }

    switch (resolved) {
      case 'postgres':
        return {
          len: (col: string) => `LENGTH(${col})`,
          instr: (col: string) => `POSITION(? IN ${col})`,
          substr2: (col: string, start: string) => `SUBSTRING(${col} FROM ${start})`,
          substr3: (col: string, start: string, len: string) =>
            `SUBSTRING(${col} FROM ${start} FOR ${len})`,
          year: (col: string) => `EXTRACT(YEAR FROM ${col})`,
          month: (col: string) => `EXTRACT(MONTH FROM ${col})`,
          day: (col: string) => `EXTRACT(DAY FROM ${col})`,
          hour: (col: string) => `EXTRACT(HOUR FROM ${col})`,
          minute: (col: string) => `EXTRACT(MINUTE FROM ${col})`,
          second: (col: string) => `EXTRACT(SECOND FROM ${col})`,
          floor: (col: string) => `FLOOR(${col})`,
          ceil: (col: string) => `CEILING(${col})`,
          round: (col: string, p?: string) => (p ? `ROUND(${col}, ${p})` : `ROUND(${col})`),
          concat: (a: string, b: string) => `${a} || ${b}`,
        }
      case 'mysql':
        return {
          len: (col: string) => `CHAR_LENGTH(${col})`,
          instr: (col: string) => `INSTR(${col}, ?)`,
          substr2: (col: string, start: string) => `SUBSTRING(${col}, ${start})`,
          substr3: (col: string, start: string, len: string) =>
            `SUBSTRING(${col}, ${start}, ${len})`,
          year: (col: string) => `YEAR(${col})`,
          month: (col: string) => `MONTH(${col})`,
          day: (col: string) => `DAY(${col})`,
          hour: (col: string) => `HOUR(${col})`,
          minute: (col: string) => `MINUTE(${col})`,
          second: (col: string) => `SECOND(${col})`,
          floor: (col: string) => `FLOOR(${col})`,
          ceil: (col: string) => `CEIL(${col})`,
          round: (col: string, p?: string) => (p ? `ROUND(${col}, ${p})` : `ROUND(${col})`),
          concat: (a: string, b: string) => `CONCAT(${a}, ${b})`,
        }
      case 'sqlite':
        return {
          len: (col: string) => `LENGTH(${col})`,
          instr: (col: string) => `INSTR(${col}, ?)`,
          substr2: (col: string, start: string) => `SUBSTR(${col}, ${start})`,
          substr3: (col: string, start: string, len: string) => `SUBSTR(${col}, ${start}, ${len})`,
          year: (col: string) => `CAST(STRFTIME('%Y', ${col}) AS INTEGER)`,
          month: (col: string) => `CAST(STRFTIME('%m', ${col}) AS INTEGER)`,
          day: (col: string) => `CAST(STRFTIME('%d', ${col}) AS INTEGER)`,
          hour: (col: string) => `CAST(STRFTIME('%H', ${col}) AS INTEGER)`,
          minute: (col: string) => `CAST(STRFTIME('%M', ${col}) AS INTEGER)`,
          second: (col: string) => `CAST(STRFTIME('%S', ${col}) AS INTEGER)`,
          floor: (col: string) =>
            `CASE WHEN (${col} - CAST(${col} AS INTEGER)) >= 0 THEN CAST(${col} AS INTEGER) ELSE CAST(${col} AS INTEGER) - 1 END`,
          // Ceil using SQLite semantics (CAST truncates toward zero)
          // ceil(x) = CAST(x AS INTEGER) + (x > CAST(x AS INTEGER) ? 1 : 0)
          ceil: (col: string) =>
            `CAST(${col} AS INTEGER) + CASE WHEN ${col} > CAST(${col} AS INTEGER) THEN 1 ELSE 0 END`,
          round: (col: string, p?: string) => (p ? `ROUND(${col}, ${p})` : `ROUND(${col})`),
          concat: (a: string, b: string) => `${a} || ${b}`,
        }
    }
  }

  #asSqlExpr(node: ASTNode): { sql: string; params: any[] } {
    if (node.type === 'PROPERTY') {
      return { sql: '??', params: [this.#getColumnName(node.name)] }
    }
    if (node.type === 'LITERAL') {
      return { sql: '?', params: [node.value] }
    }
    throw new E_ODATA_FILTER_UNEVALUATABLE_NODE_TYPE(node)
  }

  #translate(dbQuery: DatabaseQueryBuilderContract): DatabaseQueryBuilderContract {
    this.#tokens = this.#tokenize(this.#filter)
    this.#position = 0

    if (this.#tokens.length === 0) {
      return dbQuery
    }

    const ast = this.#parseExpression()
    return this.#applyASTToQuery(ast, dbQuery)
  }

  #tokenize(input: string): Token[] {
    const tokens: Token[] = []
    let i = 0

    while (i < input.length) {
      // Skip whitespace
      if (/\s/.test(input[i])) {
        while (i < input.length && /\s/.test(input[i])) i++
        continue
      }

      // Parameter reference (@param)
      if (input[i] === '@') {
        let j = i + 1
        while (j < input.length && /[a-zA-Z0-9_]/.test(input[j])) j++
        if (j === i + 1) {
          // No parameter name after @
          throw new E_ODATA_FILTER_UNEXPECTED_CHARACTER(input[i], i)
        }
        tokens.push({ type: 'PARAMETER', value: input.substring(i + 1, j) })
        i = j
        continue
      }

      // String literals
      if (input[i] === "'") {
        let j = i + 1
        let value = ''
        let closed = false
        while (j < input.length) {
          if (input[j] === "'") {
            if (j + 1 < input.length && input[j + 1] === "'") {
              value += "'"
              j += 2
            } else {
              j++
              closed = true
              break
            }
          } else {
            value += input[j]
            j++
          }
        }
        if (!closed) {
          throw new E_ODATA_FILTER_UNEXPECTED_END_OF_INPUT()
        }
        tokens.push({ type: 'STRING', value })
        i = j
        continue
      }

      // Numbers (including decimals and scientific notation)
      if (
        /[0-9]/.test(input[i]) ||
        (input[i] === '-' && i + 1 < input.length && /[0-9]/.test(input[i + 1]))
      ) {
        let j = i
        if (input[j] === '-') j++
        while (j < input.length && /[0-9]/.test(input[j])) j++
        if (j < input.length && input[j] === '.') {
          j++
          while (j < input.length && /[0-9]/.test(input[j])) j++
        }
        if (j < input.length && /[eE]/.test(input[j])) {
          j++
          if (j < input.length && /[+-]/.test(input[j])) j++
          while (j < input.length && /[0-9]/.test(input[j])) j++
        }
        tokens.push({ type: 'NUMBER', value: input.substring(i, j) })
        i = j
        continue
      }

      // Parentheses
      if (input[i] === '(') {
        tokens.push({ type: 'LPAREN', value: '(' })
        i++
        continue
      }
      if (input[i] === ')') {
        tokens.push({ type: 'RPAREN', value: ')' })
        i++
        continue
      }

      // Comma
      if (input[i] === ',') {
        tokens.push({ type: 'COMMA', value: ',' })
        i++
        continue
      }

      // Identifiers and keywords
      if (/[a-zA-Z_$]/.test(input[i])) {
        let j = i
        while (j < input.length && /[a-zA-Z0-9_$\/]/.test(input[j])) j++
        const value = input.substring(i, j)

        // Check for boolean literals
        if (value === 'true' || value === 'false') {
          tokens.push({ type: 'BOOLEAN', value })
        }
        // Check for null literal
        else if (value === 'null') {
          tokens.push({ type: 'NULL', value })
        }
        // Check for operators
        else if (
          [
            ...ODataToLucidTranslator.#LOGICAL_OPERATORS,
            ...ODataToLucidTranslator.#COMPARISON_OPERATORS,
          ].includes(value.toLowerCase())
        ) {
          tokens.push({ type: 'OPERATOR', value: value.toLowerCase() })
        }
        // Otherwise it's an identifier
        else {
          tokens.push({ type: 'IDENTIFIER', value })
        }
        i = j
        continue
      }

      // If we get here, we have an unexpected character
      throw new E_ODATA_FILTER_UNEXPECTED_CHARACTER(input[i], i)
    }

    return tokens
  }

  #peek(): Token | null {
    if (this.#position >= this.#tokens.length) return null
    return this.#tokens[this.#position]
  }

  #consume(): Token | null {
    if (this.#position >= this.#tokens.length) return null
    return this.#tokens[this.#position++]
  }

  #expect(type: Token['type'], value?: string): Token {
    const token = this.#consume()
    if (!token || token.type !== type || (value !== undefined && token.value !== value)) {
      throw new E_ODATA_FILTER_UNEXPECTED_TYPE(type, token, value)
    }
    return token
  }

  #parseExpression(): ASTNode {
    return this.#parseLogicalOr()
  }

  #parseLogicalOr(): ASTNode {
    let left = this.#parseLogicalAnd()

    while (this.#peek()?.type === 'OPERATOR' && this.#peek()?.value === 'or') {
      this.#consume()
      const right = this.#parseLogicalAnd()
      left = { type: 'LOGICAL', operator: 'or', left, right }
    }

    return left
  }

  #parseLogicalAnd(): ASTNode {
    let left = this.#parseNot()

    while (this.#peek()?.type === 'OPERATOR' && this.#peek()?.value === 'and') {
      this.#consume()
      const right = this.#parseNot()
      left = { type: 'LOGICAL', operator: 'and', left, right }
    }

    return left
  }

  #parseNot(): ASTNode {
    if (this.#peek()?.type === 'OPERATOR' && this.#peek()?.value === 'not') {
      this.#consume()
      return { type: 'LOGICAL', operator: 'not', operand: this.#parseNot() }
    }

    return this.#parseComparison()
  }

  #parseComparison(): ASTNode {
    let left = this.#parsePrimary()

    const token = this.#peek()
    if (
      token?.type === 'OPERATOR' &&
      ODataToLucidTranslator.#COMPARISON_OPERATORS.includes(token.value)
    ) {
      const operator = this.#consume()!.value

      if (operator === 'in') {
        // Handle 'in' operator which expects a list
        this.#expect('LPAREN')
        const values: ASTNode[] = []
        if (this.#peek()?.type !== 'RPAREN') {
          values.push(this.#parsePrimary())
          while (this.#peek()?.type === 'COMMA') {
            this.#consume()
            values.push(this.#parsePrimary())
          }
        }
        this.#expect('RPAREN')
        return { type: 'COMPARISON', operator: 'in', left, right: values }
      } else {
        const right = this.#parsePrimary()
        return { type: 'COMPARISON', operator, left, right }
      }
    }

    return left
  }

  #parsePrimary(): ASTNode {
    const token = this.#peek()

    if (!token) {
      throw new E_ODATA_FILTER_UNEXPECTED_END_OF_INPUT()
    }

    // Parenthesized expression
    if (token.type === 'LPAREN') {
      this.#consume()
      const expr = this.#parseExpression()
      this.#expect('RPAREN')
      return expr
    }

    // Function call
    if (token.type === 'IDENTIFIER') {
      const nextToken = this.#tokens[this.#position + 1]
      if (nextToken?.type === 'LPAREN') {
        return this.#parseFunctionCall()
      }
      // Property reference
      const identifier = this.#consume()!
      return { type: 'PROPERTY', name: identifier.value }
    }

    // String literal
    if (token.type === 'STRING') {
      this.#consume()
      return { type: 'LITERAL', dataType: 'string', value: token.value }
    }

    // Number literal
    if (token.type === 'NUMBER') {
      this.#consume()
      return { type: 'LITERAL', dataType: 'number', value: Number.parseFloat(token.value) }
    }

    // Boolean literal
    if (token.type === 'BOOLEAN') {
      this.#consume()
      return { type: 'LITERAL', dataType: 'boolean', value: token.value === 'true' }
    }

    // Null literal
    if (token.type === 'NULL') {
      this.#consume()
      return { type: 'LITERAL', dataType: 'null', value: null }
    }

    // Parameter reference
    if (token.type === 'PARAMETER') {
      this.#consume()
      const aliasValue = this.#aliases.get(token.value)
      if (!aliasValue) {
        throw new E_ODATA_UNKNOWN_PARAMETER_ALIAS(token.value)
      }
      // Parse the alias value as a literal
      return this.#parseLiteralValue(aliasValue)
    }
    throw new E_ODATA_FILTER_UNEXPECTED_TOKEN(token)
  }

  #parseFunctionCall(): ASTNode {
    const funcName = this.#consume()!.value.toLowerCase()
    this.#expect('LPAREN')

    const args: ASTNode[] = []
    if (this.#peek()?.type !== 'RPAREN') {
      args.push(this.#parseExpression())
      while (this.#peek()?.type === 'COMMA') {
        this.#consume()
        args.push(this.#parseExpression())
      }
    }

    this.#expect('RPAREN')
    return { type: 'FUNCTION', name: funcName, arguments: args }
  }

  #parseLiteralValue(value: string): ASTNode {
    // Try to parse as number
    if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(value)) {
      return { type: 'LITERAL', dataType: 'number', value: Number.parseFloat(value) }
    }

    // Try to parse as boolean
    if (value === 'true' || value === 'false') {
      return { type: 'LITERAL', dataType: 'boolean', value: value === 'true' }
    }

    // Try to parse as null
    if (value === 'null') {
      return { type: 'LITERAL', dataType: 'null', value: null }
    }

    // Otherwise treat as string (remove quotes if present)
    if (value.startsWith("'") && value.endsWith("'")) {
      value = value.slice(1, -1).replace(/''/g, "'")
    }

    return { type: 'LITERAL', dataType: 'string', value }
  }

  #applyASTToQuery(
    node: ASTNode,
    query: DatabaseQueryBuilderContract
  ): DatabaseQueryBuilderContract {
    // Treat bare boolean-returning expressions as "eq true"
    if (node.type === 'PROPERTY') {
      return this.#applyComparisonOperator(
        {
          type: 'COMPARISON',
          operator: 'eq',
          left: node,
          right: { type: 'LITERAL', dataType: 'boolean', value: true },
        },
        query
      )
    }
    if (node.type === 'FUNCTION') {
      return this.#applyComparisonOperator(
        {
          type: 'COMPARISON',
          operator: 'eq',
          left: node,
          right: { type: 'LITERAL', dataType: 'boolean', value: true },
        },
        query
      )
    }

    switch (node.type) {
      case 'LOGICAL':
        return this.#applyLogicalOperator(node, query)
      case 'COMPARISON':
        return this.#applyComparisonOperator(node, query)
      default:
        throw new E_ODATA_FILTER_INAPPLICABLE_NODE_TYPE(node)
    }
  }

  #applyLogicalOperator(
    node: ASTNode,
    query: DatabaseQueryBuilderContract
  ): DatabaseQueryBuilderContract {
    switch (node.operator) {
      case 'and':
        return query
          .where((subQuery) => {
            this.#applyASTToQuery(node.left, subQuery)
          })
          .where((subQuery) => {
            this.#applyASTToQuery(node.right, subQuery)
          })

      case 'or':
        return query.where((subQuery) => {
          subQuery
            .where((sq) => this.#applyASTToQuery(node.left, sq))
            .orWhere((sq) => this.#applyASTToQuery(node.right, sq))
        })

      case 'not':
        return query.whereNot((subQuery) => {
          this.#applyASTToQuery(node.operand, subQuery)
        })

      default:
        throw new E_ODATA_FILTER_UNKNOWN_LOGICAL_OPERATOR(node)
    }
  }

  #applyComparisonOperator(
    node: ASTNode,
    query: DatabaseQueryBuilderContract
  ): DatabaseQueryBuilderContract {
    const left = this.#evaluateNode(node.left)
    const right =
      node.operator === 'in'
        ? node.right.map((n: ASTNode) => this.#evaluateNode(n))
        : this.#evaluateNode(node.right)

    // If left side is a column reference
    if (typeof left === 'object' && left.type === 'column') {
      const column = this.#getColumnName(left.name)

      if (node.operator === 'eq' && right === null) return query.whereNull(column)
      if (node.operator === 'ne' && right === null) return query.whereNotNull(column)

      switch (node.operator) {
        case 'eq':
          return right === null ? query.whereNull(column) : query.where(column, '=', right)
        case 'ne':
          return right === null ? query.whereNotNull(column) : query.where(column, '!=', right)
        case 'gt':
          return query.where(column, '>', right)
        case 'ge':
          return query.where(column, '>=', right)
        case 'lt':
          return query.where(column, '<', right)
        case 'le':
          return query.where(column, '<=', right)
        case 'in':
          return query.whereIn(column, right as any[])
        case 'has':
          // OData 'has' means: all flags in the RHS are present in LHS
          // That's a bitmask test: (col & mask) = mask
          const mask = right
          return query.whereRaw('(?? & ?) = ?', [column, mask, mask])
        default:
          throw new E_ODATA_FILTER_UNKNOWN_COMPARISON_OPERATOR(node)
      }
    }

    // Support normalized comparisons where the column is on the RIGHT side
    // Example: `null eq Prop` or `5 gt Age`
    if (
      !(typeof left === 'object' && left.type === 'column') &&
      typeof right === 'object' &&
      right.type === 'column'
    ) {
      const column = this.#getColumnName(right.name)
      const value = left

      switch (node.operator) {
        case 'eq':
          return value === null ? query.whereNull(column) : query.where(column, '=', value)
        case 'ne':
          return value === null ? query.whereNotNull(column) : query.where(column, '!=', value)
        case 'gt':
          // value > column  => column < value
          return query.where(column, '<', value)
        case 'ge':
          // value >= column => column <= value
          return query.where(column, '<=', value)
        case 'lt':
          // value < column  => column > value
          return query.where(column, '>', value)
        case 'le':
          // value <= column => column >= value
          return query.where(column, '>=', value)
        // 'in' and 'has' are not meaningful with column on RHS in our context
        default:
          throw new E_ODATA_FILTER_UNKNOWN_COMPARISON_OPERATOR(node)
      }
    }

    // If it's a function comparison, we need to handle it differently
    if (typeof left === 'object' && left.type === 'function') {
      return this.#applyFunctionComparison(left, node.operator, right, query)
    }

    throw new E_ODATA_FILTER_INVALID_COMPARISON_SIDE()
  }

  #evaluateNode(node: ASTNode): any {
    switch (node.type) {
      case 'LITERAL':
        return node.value

      case 'PROPERTY':
        return { type: 'column', name: node.name }

      case 'FUNCTION':
        return this.#evaluateFunction(node)

      default:
        throw new E_ODATA_FILTER_UNEVALUATABLE_NODE_TYPE(node)
    }
  }

  #evaluateFunction(node: ASTNode): any {
    const funcName = node.name.toLowerCase()

    // For functions that need special handling in SQL
    if (
      ODataToLucidTranslator.#STRING_FUNCTIONS.includes(funcName) ||
      ODataToLucidTranslator.#DATE_FUNCTIONS.includes(funcName) ||
      ODataToLucidTranslator.#MATH_FUNCTIONS.includes(funcName)
    ) {
      return { type: 'function', name: funcName, arguments: node.arguments }
    }

    // For functions that can be evaluated immediately
    if (funcName === 'tolower' && node.arguments.length === 1) {
      const arg = this.#evaluateNode(node.arguments[0])
      if (typeof arg === 'string') return arg.toLowerCase()
    }

    if (funcName === 'toupper' && node.arguments.length === 1) {
      const arg = this.#evaluateNode(node.arguments[0])
      if (typeof arg === 'string') return arg.toUpperCase()
    }

    return { type: 'function', name: funcName, arguments: node.arguments }
  }

  #escapeLike(v: string, esc = '\\') {
    return v.replace(/[\\%_]/g, (m) => esc + m)
  }

  #dialect(query: DatabaseQueryBuilderContract) {
    const client =
      (query as any).client?.client?.config?.client || (query as any).client?.dialect?.name
    // knex names: 'pg', 'mysql', 'mysql2', 'sqlite3'
    return client
  }

  #likeOp(query: DatabaseQueryBuilderContract) {
    return this.#dialect(query) === 'pg' ? 'ILIKE' : 'LIKE'
  }

  #applyFunctionComparison(
    func: any,
    operator: string,
    value: any,
    query: DatabaseQueryBuilderContract
  ): DatabaseQueryBuilderContract {
    const funcName = func.name.toLowerCase()

    // String functions
    if (funcName === 'contains' && func.arguments.length === 2) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const val = String(this.#evaluateNode(func.arguments[1]) ?? '')
      const patt = `%${this.#escapeLike(val)}%`
      const like = this.#likeOp(query)
      /*
      |--------------------------------------------------------------------------
      | MySQL doesn't support ESCAPE clause in LIKE statements
      |--------------------------------------------------------------------------
      */
      const escapeClause = this.#dialectType === 'mysql' ? '' : ` ESCAPE '\\'`

      if (operator === 'eq') {
        return value === true
          ? query.whereRaw(`?? ${like} ?${escapeClause}`, [column, patt])
          : query.whereRaw(`NOT (?? ${like} ?${escapeClause})`, [column, patt])
      }
    }
    if (funcName === 'startswith' && func.arguments.length === 2) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const val = String(this.#evaluateNode(func.arguments[1]) ?? '')
      const patt = `${this.#escapeLike(val)}%`
      const like = this.#likeOp(query)
      /*
      |--------------------------------------------------------------------------
      | MySQL doesn't support ESCAPE clause in LIKE statements
      |--------------------------------------------------------------------------
      */
      const escapeClause = this.#dialectType === 'mysql' ? '' : ` ESCAPE '\\'`
      if (operator === 'eq') {
        return value === true
          ? query.whereRaw(`?? ${like} ?${escapeClause}`, [column, patt])
          : query.whereRaw(`NOT (?? ${like} ?${escapeClause})`, [column, patt])
      }
    }

    if (funcName === 'endswith' && func.arguments.length === 2) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const val = String(this.#evaluateNode(func.arguments[1]) ?? '')
      const patt = `%${this.#escapeLike(val)}`
      const like = this.#likeOp(query)
      /*
      |--------------------------------------------------------------------------
      | MySQL doesn't support ESCAPE clause in LIKE statements
      |--------------------------------------------------------------------------
      */
      const escapeClause = this.#dialectType === 'mysql' ? '' : ` ESCAPE '\\'`
      if (operator === 'eq') {
        return value === true
          ? query.whereRaw(`?? ${like} ?${escapeClause}`, [column, patt])
          : query.whereRaw(`NOT (?? ${like} ?${escapeClause})`, [column, patt])
      }
    }

    if (funcName === 'length' && func.arguments.length === 1) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      const f = this.#getSqlFns()
      return query.whereRaw(`${f.len('??')} ${sqlOp} ?`, [column, value])
    }

    if (funcName === 'indexof' && func.arguments.length === 2) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const searchValue = this.#evaluateNode(func.arguments[1])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      const f = this.#getSqlFns()
      // OData wants 0-based; INSTR/POSITION are 1-based (or 0 on miss). Normalize.
      // Parameter ordering differs by dialect (POSITION(? IN col) vs INSTR(col, ?)).
      const isPg = this.#dialectType === 'postgres'
      const params = isPg ? [searchValue, column, value] : [column, searchValue, value]
      return query.whereRaw(`(${f.instr('??')} - 1) ${sqlOp} ?`, params)
    }

    if (funcName === 'substring' && func.arguments.length >= 2) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const startIndex = this.#evaluateNode(func.arguments[1])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      const f = this.#getSqlFns()

      if (func.arguments.length === 2) {
        // substring(string, start) - from start to end (OData start is 0-based → SQL start is 1-based)
        return query.whereRaw(`${f.substr2('??', '(? + 1)')} ${sqlOp} ?`, [
          column,
          startIndex,
          value,
        ])
      } else if (func.arguments.length === 3) {
        // substring(string, start, length)
        const length = this.#evaluateNode(func.arguments[2])
        return query.whereRaw(`${f.substr3('??', '(? + 1)', '?')} ${sqlOp} ?`, [
          column,
          startIndex,
          length,
          value,
        ])
      }
    }

    if (funcName === 'trim' && func.arguments.length === 1) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      return query.whereRaw(`TRIM(??) ${sqlOp} ?`, [column, value])
    }

    if (funcName === 'concat' && func.arguments.length === 2) {
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      const f = this.#getSqlFns()
      // Allow column/column and column/literal combinations
      const a = this.#asSqlExpr(func.arguments[0])
      const b = this.#asSqlExpr(func.arguments[1])
      return query.whereRaw(`${f.concat(a.sql, b.sql)} ${sqlOp} ?`, [
        ...a.params,
        ...b.params,
        value,
      ])
    }

    // tolower/toupper functions for columns
    if (funcName === 'tolower' && func.arguments.length === 1) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      return query.whereRaw(`LOWER(??) ${sqlOp} LOWER(?)`, [column, value])
    }

    if (funcName === 'toupper' && func.arguments.length === 1) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      return query.whereRaw(`UPPER(??) ${sqlOp} UPPER(?)`, [column, value])
    } // Date functions
    if (ODataToLucidTranslator.#DATE_FUNCTIONS.includes(funcName) && func.arguments.length === 1) {
      const column = this.#getColumnFromNode(func.arguments[0])
      const sqlOp = this.#getComparisonOperatorSQL(operator)
      const f = this.#getSqlFns()
      let expr: string
      switch (funcName) {
        case 'year':
          expr = f.year('??')
          break
        case 'month':
          expr = f.month('??')
          break
        case 'day':
          expr = f.day('??')
          break
        case 'hour':
          expr = f.hour('??')
          break
        case 'minute':
          expr = f.minute('??')
          break
        case 'second':
          expr = f.second('??')
          break
        default:
          throw new E_ODATA_FILTER_UNSUPPORTED_FUNCTION_COMPARISON(funcName)
      }
      return query.whereRaw(`${expr} ${sqlOp} ?`, [column, value])
    }

    // Math functions
    if (ODataToLucidTranslator.#MATH_FUNCTIONS.includes(funcName)) {
      const f = this.#getSqlFns()
      const sqlOp = this.#getComparisonOperatorSQL(operator)

      if (funcName === 'round' && (func.arguments.length === 1 || func.arguments.length === 2)) {
        const column = this.#getColumnFromNode(func.arguments[0])
        if (func.arguments.length === 1) {
          // round(value) - round to integer
          return query.whereRaw(`${f.round('??')} ${sqlOp} ?`, [column, value])
        } else {
          // round(value, precision)
          const precision = this.#evaluateNode(func.arguments[1])
          return query.whereRaw(`${f.round('??', '?')} ${sqlOp} ?`, [column, precision, value])
        }
      } else if (funcName === 'floor' && func.arguments.length === 1) {
        const column = this.#getColumnFromNode(func.arguments[0])
        return query.whereRaw(`${f.floor('??')} ${sqlOp} ?`, [column, value])
      } else if (funcName === 'ceiling' && func.arguments.length === 1) {
        const column = this.#getColumnFromNode(func.arguments[0])
        return query.whereRaw(`${f.ceil('??')} ${sqlOp} ?`, [column, value])
      }
    }
    throw new E_ODATA_FILTER_UNSUPPORTED_FUNCTION_COMPARISON(funcName)
  }

  #getColumnFromNode(node: ASTNode): string {
    const result = this.#evaluateNode(node)
    if (typeof result === 'object' && result.type === 'column') {
      return this.#getColumnName(result.name)
    }
    throw new E_ODATA_FILTER_EXPECTED_COLUMN_REFERENCE()
  }

  //   #getValueFromNode(node: ASTNode): string | number {
  //     const result = this.#evaluateNode(node)
  //     if (typeof result === 'object' && result.type === 'column') {
  //       return this.#getColumnName(result.name)
  //     }
  //     // Return the literal value for non-column arguments
  //     return result
  //   }

  #getColumnName(key: string): string {
    // Check if there's a mapping for this key
    if (this.#keyToColumn.has(key)) {
      return this.#keyToColumn.get(key)!
    }
    // Otherwise use the key as-is
    return key
  }

  #getComparisonOperatorSQL(operator: string): string {
    const mapping: Record<string, string> = {
      eq: '=',
      ne: '!=',
      gt: '>',
      ge: '>=',
      lt: '<',
      le: '<=',
    }
    return mapping[operator] || '='
  }

  // (date/math helper mappings removed in favor of dialect-aware #getSqlFns())
}
